import { dealApprovalApi } from '@/services/api/deal-approval.api';
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { doc, getDoc, updateDoc, serverTimestamp, collection, addDoc, query, orderBy, getDocs, limit } from "firebase/firestore";
import { db, auth } from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { invokeFunction, invokeFunctionSilent } from "@/lib/cloudrun";
import {
  Save, Send, Eye, FileText, DollarSign, CheckCircle,
  AlertCircle, Loader2, RefreshCw, Sparkles, ArrowLeft
} from "lucide-react";
import { formatCurrency } from "@/utils/financial-analysis";
import { CIMVersionHistory, CIMVersion } from "@/components/cim/CIMVersionHistory";
import { CIMTemplateSelector } from "@/components/cim/CIMTemplateSelector";
import { CIMTemplate, getTemplateForIndustry } from "@/constants/cim-templates.constants";
import { StaffDscrEvaluator } from "@/components/staff/StaffDscrEvaluator";
 
const translations = {
  en: {
    title: "CIM Review", editor: "Editor", preview: "Preview",
    financialAdjustments: "Financial Adjustments", reportedEbitda: "Reported EBITDA",
    addbacks: "Total Addbacks", adjustedEbitda: "Adjusted EBITDA",
    ebitdaMultiple: "EBITDA Multiple", valuationRange: "Valuation Range",
    executiveSummary: "Executive Summary", businessOverview: "Business Overview",
    financialHighlights: "Financial Highlights", growthOpportunities: "Growth Opportunities",
    investmentConsiderations: "Investment Considerations", transitionPlan: "Transition Plan",
    saveChanges: "Save Changes", sendToClient: "Send to Client", approve: "Approve",
    regenerate: "Regenerate with AI", regenerateSection: "Regenerate Section",
    regenerateAll: "Regenerate All Sections", regenerating: "Regenerating...",
    status: "Status", draftInternal: "Internal Draft", pendingReview: "Pending Review",
    approved: "Approved", sentToClient: "Sent to Client", saved: "Changes saved successfully",
    approved_msg: "CIM approved successfully", sent: "CIM sent to client",
    loading: "Loading CIM data...", notFound: "CIM not found",
    low: "Low", mid: "Mid", high: "High",
    regenerated: "Section regenerated successfully",
    regeneratedAll: "All sections regenerated successfully",
    back: "Back to List", selectSection: "Select section to regenerate",
  },
  fr: {
    title: "Révision CIM", editor: "Éditeur", preview: "Prévisualisation",
    financialAdjustments: "Ajustements Financiers", reportedEbitda: "EBITDA Déclaré",
    addbacks: "Total Addbacks", adjustedEbitda: "EBITDA Ajusté",
    ebitdaMultiple: "Multiple EBITDA", valuationRange: "Fourchette de Valorisation",
    executiveSummary: "Résumé Exécutif", businessOverview: "Aperçu de l'Entreprise",
    financialHighlights: "Points Financiers Clés", growthOpportunities: "Opportunités de Croissance",
    investmentConsiderations: "Considérations d'Investissement", transitionPlan: "Plan de Transition",
    saveChanges: "Enregistrer", sendToClient: "Envoyer au Client", approve: "Approuver",
    regenerate: "Régénérer avec l'IA", regenerateSection: "Régénérer Section",
    regenerateAll: "Régénérer Tout", regenerating: "Régénération...",
    status: "Statut", draftInternal: "Brouillon Interne", pendingReview: "En Attente de Révision",
    approved: "Approuvé", sentToClient: "Envoyé au Client", saved: "Modifications enregistrées",
    approved_msg: "CIM approuvé avec succès", sent: "CIM envoyé au client",
    loading: "Chargement du CIM...", notFound: "CIM non trouvé",
    low: "Bas", mid: "Moyen", high: "Haut",
    regenerated: "Section régénérée avec succès",
    regeneratedAll: "Toutes les sections régénérées",
    back: "Retour à la liste", selectSection: "Sélectionner section à régénérer",
  },
};
 
interface CIMData {
  dealId: string;
  content: {
    executiveSummary: string; businessOverview: string; financialHighlights: string;
    growthOpportunities: string; investmentConsiderations: string; transitionPlan: string;
  };
  financialSummary: {
    reportedEbitda: number; totalAddbacks: number; adjustedEbitda: number;
    ebitdaMultiple: number; valuationLow: number; valuationMid: number; valuationHigh: number;
  };
  status: "draft_internal" | "pending_review" | "approved" | "sent_to_client";
}
 
export default function CIMReviewStaff() {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { toast } = useToast();
  const t = translations[language as keyof typeof translations] || translations.en;
 
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regeneratingSection, setRegeneratingSection] = useState<string | null>(null);
  const [cimData, setCimData] = useState<CIMData | null>(null);
  const [dealInfo, setDealInfo] = useState<any>(null);
  const [referenceNumber, setReferenceNumber] = useState<string | null>(null);
  const [versionNumber, setVersionNumber] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState("default");
 
  const [content, setContent] = useState({
    executiveSummary: "", businessOverview: "", financialHighlights: "",
    growthOpportunities: "", investmentConsiderations: "", transitionPlan: "",
  });
 
  const [financials, setFinancials] = useState({
    reportedEbitda: 0, totalAddbacks: 0, adjustedEbitda: 0,
    ebitdaMultiple: 0, valuationLow: 0, valuationMid: 0, valuationHigh: 0,
  });
 
  useEffect(() => { if (dealId) loadCIMData(); }, [dealId]);
 
  const loadCIMData = async () => {
    if (!dealId) return;
    try {
      setLoading(true);
 
      const cimDoc = await getDoc(doc(db, "cimData", dealId));
      if (cimDoc.exists()) {
        const data = cimDoc.data() as CIMData;
        // Défend contre un CIM partiellement généré (ex. échec de parsing
        // JSON côté generateCimNarrative, tombé en mode placeholder) où
        // financialSummary pourrait être absent malgré un document existant.
        const safeFinancials = data.financialSummary || {
          reportedEbitda: 0, totalAddbacks: 0, adjustedEbitda: 0,
          ebitdaMultiple: 0, valuationLow: 0, valuationMid: 0, valuationHigh: 0,
        };
        setCimData({ ...data, financialSummary: safeFinancials });
        setContent(data.content || {
          executiveSummary: "", businessOverview: "", financialHighlights: "",
          growthOpportunities: "", investmentConsiderations: "", transitionPlan: "",
        });
        setFinancials(safeFinancials);
      }
 
      const dealDoc = await getDoc(doc(db, "dealSubmissions", dealId));
      if (dealDoc.exists()) {
        const info = dealDoc.data();
        setDealInfo(info);
        if (info.general?.industry) {
          setSelectedTemplate(info.general.industry.toLowerCase().replace(/[^a-z]/g, '_'));
        }
      }
 
      const versionsRef = collection(db, "cimData", dealId, "versions");
      const versionsQuery = query(versionsRef, orderBy("versionNumber", "desc"), limit(1));
      const versionsSnapshot = await getDocs(versionsQuery);
      if (!versionsSnapshot.empty) {
        setVersionNumber(versionsSnapshot.docs[0].data().versionNumber + 1);
      }
 
      // Remplace : supabase.from('deals').select('reference_number').eq('id', dealId)
      // Le reference_number est maintenant dans Firestore 'deals'
      const dealsDoc = await getDoc(doc(db, "deals", dealId));
      if (dealsDoc.exists() && dealsDoc.data().reference_number) {
        setReferenceNumber(dealsDoc.data().reference_number);
      }
    } catch (error) {
      console.error("Error loading CIM:", error);
      toast({ title: "Error", description: "Failed to load CIM data", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };
 
  const saveVersion = async (saveType: "manual" | "auto" | "ai_generated" = "manual") => {
    if (!dealId) return;
    try {
      const versionsRef = collection(db, "cimData", dealId, "versions");
      await addDoc(versionsRef, {
        versionNumber, content, financialSummary: financials,
        savedAt: serverTimestamp(), saveType,
      });
      setVersionNumber(prev => prev + 1);
    } catch (error) { console.error("Error saving version:", error); }
  };
 
  const handleRestoreVersion = async (version: CIMVersion) => {
    await saveVersion("auto");
    setContent(version.content);
    setFinancials(version.financialSummary);
    toast({
      title: language === "fr" ? "Version restaurée" : "Version restored",
      description: language === "fr"
        ? `Version ${version.versionNumber} restaurée avec succès`
        : `Version ${version.versionNumber} restored successfully`,
    });
  };
 
  const handleTemplateChange = (_industry: string, template: CIMTemplate) => {
    setSelectedTemplate(_industry);
    toast({ title: language === "fr" ? "Modèle sélectionné" : "Template selected", description: template.industry });
  };
 
  // Remplace : supabase.functions.invoke('generate-cim-narrative') — section
  const regenerateSection = async (section: string) => {
    if (!dealId || !dealInfo) return;
    setRegeneratingSection(section);
    try {
      const template = getTemplateForIndustry(selectedTemplate);
      const { data, error } = await invokeFunction("generate-cim-narrative", {
        dealData: dealInfo, financialAnalysis: financials, section, template,
      });
      if (error) throw error;
      if (data?.content?.[section]) {
        setContent(prev => ({ ...prev, [section]: data.content[section] }));
        toast({ title: t.regenerated });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to regenerate section", variant: "destructive" });
    } finally { setRegeneratingSection(null); }
  };
 
  // Remplace : supabase.functions.invoke('generate-cim-narrative') — all
  const regenerateAllSections = async () => {
    if (!dealId || !dealInfo) return;
    setRegenerating(true);
    try {
      const template = getTemplateForIndustry(selectedTemplate);
      const { data, error } = await invokeFunction("generate-cim-narrative", {
        dealData: dealInfo, financialAnalysis: financials, template,
      });
      if (error) throw error;
      if (data?.content) {
        await saveVersion("ai_generated");
        setContent(data.content);
        toast({ title: t.regeneratedAll });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to regenerate CIM", variant: "destructive" });
    } finally { setRegenerating(false); }
  };
 
  const handleSendForSellerApproval = async () => {
  if (!dealId) return;
  try {
    setSaving(true);
    await dealApprovalApi.sendForApproval(dealId);
    toast({
      title: language === 'fr'
        ? 'Envoyé au vendeur pour approbation'
        : 'Sent to seller for approval',
      description: language === 'fr'
        ? 'Le vendeur recevra un courriel avec le lien d\'approbation.'
        : 'The seller will receive an email with the approval link.',
    });
  } catch (err: any) {
    toast({
      title: language === 'fr' ? 'Erreur' : 'Error',
      description: err?.message || 'Failed to send for approval',
      variant: 'destructive',
    });
  } finally {
    setSaving(false);
  }
};

const handleSendToLendersByod = async () => {
    if (!dealId) return;
    try {
      setSaving(true);
      // Get eligible lenders count first
      const countRes = await fetch(
        `${import.meta.env.VITE_API_URL}/deals/${dealId}/eligible-lenders-count`,
        { headers: { Authorization: `Bearer ${await auth.currentUser?.getIdToken()}` } }
      );
      const { count } = await countRes.json();
      
      const confirmed = window.confirm(
        language === 'fr'
          ? `Ce CIM sera envoyé à ${count} prêteur(s) éligible(s). Le deal restera confidentiel (non publié sur le Deal Monitor). Confirmer?`
          : `This CIM will be sent to ${count} eligible lender(s). The deal will remain confidential (not published on the Deal Monitor). Confirm?`
      );
      if (!confirmed) return;

      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/deals/${dealId}/send-to-lenders-byod`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
          },
        }
      );
      const result = await res.json();
      toast({
        title: language === 'fr' ? `Envoyé à ${result.sent} prêteur(s)` : `Sent to ${result.sent} lender(s)`,
        description: language === 'fr'
          ? 'Les prêteurs recevront une notification dans leur tableau de bord.'
          : 'Lenders will receive a notification in their dashboard.',
      });
    } catch (err: any) {
      toast({
        title: language === 'fr' ? 'Erreur' : 'Error',
        description: err?.message || 'Failed to send to lenders',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!dealId) return;
    setSaving(true);
    try {
      await saveVersion("manual");
      await updateDoc(doc(db, "cimData", dealId), {
        content, financialSummary: financials, updatedAt: serverTimestamp(),
      });
      toast({ title: t.saved });
    } catch (error) {
      toast({ title: "Error", description: "Failed to save changes", variant: "destructive" });
    } finally { setSaving(false); }
  };
 
  // Remplace : supabase.functions.invoke('send-notification-email')
  const handleStatusChange = async (newStatus: CIMData["status"]) => {
    if (!dealId) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, "cimData", dealId), { status: newStatus, updatedAt: serverTimestamp() });
      await invokeFunctionSilent("send-notification-email", {
        type: newStatus === "approved" ? "cim_approved" : "cim_sent_to_client",
        dealId,
        dealName: dealInfo?.general?.tradeName || dealInfo?.general?.legalName,
      });
      setCimData(prev => prev ? { ...prev, status: newStatus } : null);
      toast({ title: newStatus === "approved" ? t.approved_msg : t.sent });
    } catch (error) {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    } finally { setSaving(false); }
  };
 
  const handleAddbackChange = (value: number) => {
    const newAdjustedEbitda = financials.reportedEbitda + value;
    const askingPrice = dealInfo?.financials?.askingPrice || 0;
    setFinancials({
      ...financials, totalAddbacks: value, adjustedEbitda: newAdjustedEbitda,
      ebitdaMultiple: parseFloat((newAdjustedEbitda > 0 ? askingPrice / newAdjustedEbitda : 0).toFixed(2)),
    });
  };
 
  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      draft_internal: "secondary", pending_review: "outline", approved: "default", sent_to_client: "default",
    };
    const labels: Record<string, string> = {
      draft_internal: t.draftInternal, pending_review: t.pendingReview,
      approved: t.approved, sent_to_client: t.sentToClient,
    };
    return <Badge variant={variants[status]}>{labels[status]}</Badge>;
  };
 
  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <span className="ml-2">{t.loading}</span>
    </div>
  );
 
  if (!cimData) return (
    <div className="flex flex-col items-center justify-center h-64">
      <AlertCircle className="h-12 w-12 text-muted-foreground" />
      <p className="mt-4 text-lg">{t.notFound}</p>
    </div>
  );
 
  const sections = [
    { key: "executiveSummary", label: "Summary", title: t.executiveSummary },
    { key: "businessOverview", label: "Business", title: t.businessOverview },
    { key: "financialHighlights", label: "Financials", title: t.financialHighlights },
    { key: "growthOpportunities", label: "Growth", title: t.growthOpportunities },
    { key: "investmentConsiderations", label: "Investment", title: t.investmentConsiderations },
    { key: "transitionPlan", label: "Transition", title: t.transitionPlan },
  ];
 
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin/cim-eval")}>
            <ArrowLeft className="h-4 w-4 mr-2" />{t.back}
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{t.title}</h1>
            <p className="text-muted-foreground">
              {dealInfo?.general?.tradeName || dealInfo?.general?.legalName}
              {referenceNumber && <span className="ml-2 font-mono text-xs text-primary">({referenceNumber})</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {getStatusBadge(cimData.status)}
          {dealId && <CIMVersionHistory dealId={dealId} onRestore={handleRestoreVersion} />}
          <Button variant="outline" onClick={regenerateAllSections} disabled={regenerating}
            className="bg-purple-500/10 border-purple-500/30 text-purple-400 hover:bg-purple-500/20">
            {regenerating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
            {regenerating ? t.regenerating : t.regenerateAll}
          </Button>
          <Button variant="outline" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            {t.saveChanges}
          </Button>
          {cimData.status === "draft_internal" && (
            <Button onClick={() => handleStatusChange("approved")} disabled={saving}>
              <CheckCircle className="h-4 w-4 mr-2" />{t.approve}
            </Button>
          )}
          {cimData.status === "approved" && (
            <Button onClick={() => handleStatusChange("sent_to_client")} disabled={saving}>
              <Send className="h-4 w-4 mr-2" />{t.sendToClient}
            </Button>
          )}
        </div>
      </div>
 
 {(cimData.status === "approved" || cimData.status === "sent_to_client") && (
            <Button
              onClick={handleSendForSellerApproval}
              disabled={saving}
              style={{ background: '#1D314D', color: '#CEAB65', border: '1px solid rgba(206,171,101,0.3)' }}
            >
              <Send className="h-4 w-4 mr-2" />
              {language === 'fr' ? 'Envoyer au vendeur pour approbation' : 'Send for Seller Approval'}
            </Button>
          )}
          {(cimData.status === "approved" || cimData.status === "sent_to_client") && (
            <Button
              onClick={handleSendToLendersByod}
              disabled={saving}
              style={{ background: '#CEAB65', color: '#1D314D', border: '1px solid rgba(29,49,77,0.3)', fontWeight: 600 }}
            >
              <Send className="h-4 w-4 mr-2" />
              {language === 'fr' ? 'Envoyer aux prêteurs (BYOD)' : 'Send to Lenders (BYOD)'}
            </Button>
          )}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><DollarSign className="h-5 w-5" />{t.financialAdjustments}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><Label>{t.reportedEbitda}</Label><div className="text-lg font-semibold text-muted-foreground">{formatCurrency(financials.reportedEbitda)}</div></div>
            <div><Label>{t.addbacks}</Label><Input type="number" value={financials.totalAddbacks} onChange={(e) => handleAddbackChange(parseFloat(e.target.value) || 0)} className="mt-1" /></div>
            <div><Label>{t.adjustedEbitda}</Label><div className="text-lg font-semibold text-green-600">{formatCurrency(financials.adjustedEbitda)}</div></div>
            <div><Label>{t.ebitdaMultiple}</Label><div className="text-lg font-semibold">{financials.ebitdaMultiple.toFixed(1)}x</div></div>
          </div>
          <div className="mt-4 pt-4 border-t">
            <Label>{t.valuationRange}</Label>
            <div className="flex gap-6 mt-2">
              <div><span className="text-sm text-muted-foreground">{t.low}</span><div className="font-semibold">{formatCurrency(financials.valuationLow)}</div></div>
              <div><span className="text-sm text-muted-foreground">{t.mid}</span><div className="font-semibold text-primary">{formatCurrency(financials.valuationMid)}</div></div>
              <div><span className="text-sm text-muted-foreground">{t.high}</span><div className="font-semibold">{formatCurrency(financials.valuationHigh)}</div></div>
            </div>
          </div>
        </CardContent>
      </Card>

      {dealId && <StaffDscrEvaluator dealId={dealId} language={language as 'en' | 'fr'} />}

      <CIMTemplateSelector selectedIndustry={selectedTemplate} onSelect={handleTemplateChange} />
 
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" />{t.editor}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <Tabs defaultValue="executiveSummary">
              <TabsList className="grid grid-cols-3 lg:grid-cols-6 h-auto">
                {sections.map(s => <TabsTrigger key={s.key} value={s.key} className="text-xs">{s.label}</TabsTrigger>)}
              </TabsList>
              {sections.map(s => (
                <TabsContent key={s.key} value={s.key} className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label>{s.title}</Label>
                    <Button size="sm" variant="ghost" onClick={() => regenerateSection(s.key)}
                      disabled={regeneratingSection === s.key}
                      className="text-purple-500 hover:text-purple-600 hover:bg-purple-500/10">
                      {regeneratingSection === s.key ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                      {t.regenerateSection}
                    </Button>
                  </div>
                  <Textarea value={(content as any)[s.key]}
                    onChange={(e) => setContent(prev => ({ ...prev, [s.key]: e.target.value }))}
                    className="min-h-[300px]" />
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
 
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Eye className="h-5 w-5" />{t.preview}</CardTitle></CardHeader>
          <CardContent>
            <div className="bg-white border rounded-lg p-6 max-h-[600px] overflow-y-auto prose prose-sm dark:prose-invert">
              {sections.map(s => (
                <div key={s.key}>
                  <h2 className="text-xl font-bold border-b pb-2 mt-6">{s.title}</h2>
                  <p className="whitespace-pre-wrap">{(content as any)[s.key]}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}